"use client"

import { useState } from "react"
import Link from "next/link"
import { ResilientImage as Image } from "@/components/resilient-image"
import { Gallery } from "@/lib/photos"
import { Images, Spotlight, Trophy, Briefcase, Loader2 } from "lucide-react"
import { cn } from "@/lib/utils"

interface GalleryCardProps {
  gallery: Gallery
  priority?: boolean
  shareToken?: string
}

export function GalleryCard({ gallery, priority = false, shareToken }: GalleryCardProps) {
  const [isNavigating, setIsNavigating] = useState(false)
  // Priority (above-fold) images render at full opacity immediately — no fade-in.
  // This prevents the opacity:0 → opacity:1 transition from delaying LCP.
  // Non-priority (below-fold) images fade in as they lazy-load for a polished feel.
  const [imageLoaded, setImageLoaded] = useState(priority)

  const href = `/gallery/${gallery.id}${shareToken ? `?share=${encodeURIComponent(shareToken)}` : ''}`
  const coverImage = shareToken && gallery.coverImage
    ? `${gallery.coverImage}${gallery.coverImage.includes('?') ? '&' : '?'}share=${encodeURIComponent(shareToken)}`
    : gallery.coverImage

  return (
    <Link
      href={href}
      className="group block h-full rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      onClick={() => setIsNavigating(true)}
    >
      <div className={cn(
        "relative aspect-[4/3] w-full overflow-hidden rounded-2xl border bg-card shadow-sm transition-all duration-300 group-hover:-translate-y-0.5 group-hover:shadow-lg",
        isNavigating && "ring-2 ring-primary ring-offset-2 ring-offset-background"
      )}>
        {/* Inline base64 blur placeholder: embedded in HTML, renders instantly (zero network requests) */}
        {gallery.blurDataURL ? (
          <img
            src={gallery.blurDataURL}
            alt=""
            aria-hidden
            className="absolute inset-0 h-full w-full object-cover object-top scale-110 blur-sm"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-muted">
            <Images className="h-12 w-12 text-muted-foreground/40" />
          </div>
        )}
        {/* Full-resolution cover image: fades in over the blur placeholder */}
        {coverImage && (
          <Image
            src={coverImage}
            alt={gallery.title}
            fill
            priority={priority}
            fetchPriority={priority ? "high" : undefined}
            loading={priority ? "eager" : "lazy"}
            unoptimized
            className={cn(
              "object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]",
              // Priority images: no transition (avoids LCP delay from opacity animation)
              priority
                ? "opacity-100"
                : cn("transition-opacity duration-500", imageLoaded ? "opacity-100" : "opacity-0")
            )}
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            onLoad={() => setImageLoaded(true)}
          />
        )}

        {gallery.type === 'event' && (
          <div className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full border border-white/15 bg-black/45 px-2.5 py-1 text-xs font-medium text-white shadow-sm backdrop-blur-md">
            <Spotlight className="h-3.5 w-3.5" />
            <span>Event</span>
          </div>
        )}

        {gallery.type === 'championship' && (
          <div className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full border border-white/15 bg-black/45 px-2.5 py-1 text-xs font-medium text-white shadow-sm backdrop-blur-md">
            <Trophy className="h-3.5 w-3.5" />
            <span>Championship</span>
          </div>
        )}

        {gallery.type === 'portfolio' && (
          <div className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full border border-white/15 bg-black/45 px-2.5 py-1 text-xs font-medium text-white shadow-sm backdrop-blur-md">
            <Briefcase className="h-3.5 w-3.5" />
            <span>Portfolio</span>
          </div>
        )}

        <div className="absolute right-3 top-3 flex items-center gap-1.5 rounded-full border border-white/15 bg-black/45 px-2.5 py-1 text-xs font-medium text-white backdrop-blur-md">
          <Images className="h-3.5 w-3.5" />
          <span>{gallery.count}</span>
        </div>

        {/* Loading overlay */}
        {isNavigating && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/40 backdrop-blur-[2px]">
            <div className="flex items-center gap-2 rounded-full bg-white/90 px-4 py-2 shadow-lg dark:bg-black/80">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <span className="text-sm font-medium">Loading...</span>
            </div>
          </div>
        )}

      </div>

      <div className="px-1 pt-3">
        <h3 className="line-clamp-2 font-bebas text-2xl uppercase leading-none tracking-wide text-foreground transition-colors group-hover:text-primary">
          {gallery.title}
        </h3>

        <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 font-sans text-sm font-medium text-muted-foreground">
          <span>{new Date(gallery.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' })}</span>
          {gallery.location && <span>{gallery.location}</span>}
        </div>
      </div>
    </Link>
  )
}
